import React, { useEffect, useState } from 'react'

import { Box, MenuItem, Select, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'

function buildPageItems(page, totalPages) {
  const items = []
  const add = (val) => items.push(val)

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i += 1) add(i)
    return items
  }

  const windowStart = Math.max(2, page - 1)
  const windowEnd = Math.min(totalPages - 1, page + 1)

  add(1)
  if (windowStart > 2) add('left-ellipsis')

  for (let i = windowStart; i <= windowEnd; i += 1) add(i)

  if (windowEnd < totalPages - 1) add('right-ellipsis')
  add(totalPages)

  return items
}

function NavButton({ children, disabled, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        border: '1px solid var(--card-border)',
        background: 'transparent',
        color: 'var(--table-text)',
        borderRadius: 1,
        p: 1,
        cursor: 'pointer',
        fontSize: '0.8125rem',
        lineHeight: 1,
        '&:hover:not(:disabled)': {
          borderColor: 'var(--primary)',
          color: 'var(--primary)',
        },
        '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
      }}
    >
      {children}
    </Box>
  )
}

function PageButton({ active, disabled, onClick, children }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        border: '1px solid',
        borderColor: active ? 'var(--primary)' : 'var(--card-border)',
        background: active ? 'var(--primary)' : 'transparent',
        color: active ? '#fff' : 'var(--table-text)',
        borderRadius: 1,
        minWidth: 32,
        height: 30,
        px: 0.5,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.8125rem',
        fontWeight: active ? 600 : 400,
        opacity: disabled ? 0.4 : 1,
        '&:hover:not(:disabled)': {
          borderColor: 'var(--primary)',
          // Keep the selected page label legible: its background is already
          // the primary colour, so changing its text to primary on hover
          // makes the number disappear.
          color: active ? '#fff' : 'var(--primary)',
        },
      }}
    >
      {children}
    </Box>
  )
}

// PaginationFooter is generic — only consumes the pagination keys it owns
// from `params` and forwards everything else to the consumer's listCall
// verbatim so tables can add their own filters (`name`, `branchCode`,
// `status`, …) without this component knowing about them.

function PaginationFooter({
  listCall,
  params = {},
  setListData,
  querykey,
  listCallMode = 'Object',
  setPerPage,
  setIsLoading,
  allData,
  refreshTrigger,
  // Caller already owns the count (e.g. they pass `totalCount` from the
  // response they already fetched). When provided, this overrides the
  // component's own `useQuery` total — without this, callers using an
  // external query are stuck with "Showing 0 – 0 of 0" because the footer
  // was ignoring the prop and reading from its own (disabled) query.
  totalCount: totalCountProp,
  // External "controlled" pagination: when the caller already manages page
  // state (UserTable, Facilities), they pass `page` (0-indexed) and
  // `onPageChange` directly. These take precedence over `params.currentPage`
  // / `params.setCurrentPage` so consumers don't have to thread the setter
  // through `params`. Without this, the Previous / page-number / Next
  // buttons were wired to `setCurrentPage` from `params` (always undefined
  // for these consumers) and silently did nothing.
  page: pageProp,
  onPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  defaultLimit = 10,
  // Controlled `rowsPerPage` — when the caller already manages the page
  // size (Facilities passes the same `rowsPerPage` it uses for its own
  // `useQuery`), treat it as the source of truth for the range math +
  // dropdown. Without this, the footer's internal `limit` (initialised
  // from `defaultLimit`) stayed stuck at 10 even after the caller updated
  // the value, so the footer showed "Showing 1 – 10 of 15" while only 5
  // rows were rendered.
  rowsPerPage: rowsPerPageProp,
}) {
  const {
    currentPage,
    sortColumn,
    sortDirection,
    sortOrder,
    filterFields,
    filterInputs,
    setCurrentPage,
    search,
    // `orderBy` / `order` are the newer (preferred) sort keys read directly
    // by `listDevicesApi` (see src/pages/components/Devices/api.js). Older
    // callers still use `sortColumn` / `sortDirection`; we forward both so
    // either shape reaches the backend.
    orderBy,
    order,
  } = params

  const [limit, setLimit] = useState(params.perPage ?? defaultLimit)
  // Effective page size — caller-provided `rowsPerPageProp` wins so the
  // footer's range math + dropdown stay in lock-step with the data the
  // caller actually rendered.
  const effectiveLimit =
    typeof rowsPerPageProp === 'number' && rowsPerPageProp > 0 ? rowsPerPageProp : limit

  // Forward every consumer-supplied param to the listCall as-is. PaginationFooter
  // stays generic — table-specific filters live in each table's own listCall.
  const queryParams = {
    per_page: effectiveLimit,
    current_page: currentPage,
    sort_column: sortColumn,
    sort_direction: sortDirection,
    sortOrder,
    filterFields,
    filterInputs,
    search,
    orderBy,
    order,
  }

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    // `filterInputs` is spread into the key so a filter change (e.g. the
    // V-Tags filter modal's `couplingStatus`) creates a *new* query instead
    // of relying on `invalidateQueries` to refetch. The previous shape only
    // had `search` here, which meant React batched `setState` +
    // `invalidateQueries` together and the refetch went out with the stale
    // `queryFn` closure — i.e. the previous filter values. Hashing the
    // filter inputs into the key prevents that race entirely.
    queryKey: [
      querykey,
      limit,
      currentPage,
      sortColumn,
      sortDirection,
      sortOrder,
      orderBy,
      order,
      search,
      ...(filterInputs ?? []),
    ],
    queryFn: () => {
      if (listCallMode === 'Object') {
        return listCall(queryParams)
      }
      return listCall(queryParams)
    },
    keepPreviousData: true,
    refetchOnMount: 'always',
    enabled: typeof listCall === 'function',
    onSuccess: (response) => {
      const list = response?.data?.data || response?.data?.result || response?.data || []
      setListData(list)
    },
  })

  useEffect(() => {
    if (refreshTrigger) refetch()
  }, [refreshTrigger, refetch])

  useEffect(() => {
    if (!data) return
    const list = data?.data?.data || data?.data?.result || data?.data || []
    setListData(list)
    if (allData) allData(data)
  }, [data])

  useEffect(() => {
    if (setIsLoading) {
      setIsLoading((isLoading || isFetching) && !isError)
    }
  }, [isLoading, isFetching, isError, setIsLoading])

  if (isError) {
    return (
      <Box sx={{ p: 2, color: 'var(--danger, #ff3d57)' }}>
        Error: {error?.message || 'Failed to load data'}
      </Box>
    )
  }

  // `pageProp` / `onPageChange` form a controlled pair — when the caller
  // already owns page state (UserTable, Facilities, AccessPointsTable), they
  // pass a 0-indexed `page` and an `onPageChange` setter. We treat *both*
  // being provided as the controlled signal. Without this, the footer would
  // try to read from `params.currentPage` (always undefined for these
  // consumers) and the page buttons would silently do nothing.
  const isControlledPage = pageProp != null && typeof onPageChange === 'function'
  // Callers in controlled mode hold a 0-indexed page; the rest of the
  // rendering math (buildPageItems, start/end, etc.) expects 1-indexed, so
  // translate once here.
  const currentDisplayPage = isControlledPage ? pageProp + 1 : currentPage || 1

  const totalItems =
    (typeof totalCountProp === 'number' ? totalCountProp : null) ??
    data?.data?.total ??
    data?.data?.meta?.total ??
    data?.meta?.total ??
    0
  const totalPages = Math.ceil(totalItems / effectiveLimit) || 1
  const safePage = Math.min(Math.max(1, currentDisplayPage), totalPages)
  const start = totalItems === 0 ? 0 : (safePage - 1) * effectiveLimit + 1
  const end = Math.min(totalItems, safePage * effectiveLimit)

  const pageItems = buildPageItems(safePage, totalPages)

  const goToPage = (target) => {
    if (isControlledPage) {
      // Convert 1-indexed UI page back to 0-indexed caller state.
      onPageChange?.(Math.max(0, target - 1))
    } else {
      setCurrentPage?.(target)
    }
  }

  const handleEllipsis = (kind) => {
    if (isControlledPage) {
      if (kind === 'left-ellipsis') onPageChange?.(Math.max(0, safePage - 2))
      else if (kind === 'right-ellipsis') onPageChange?.(Math.min(totalPages - 1, safePage))
      return
    }
    if (!setCurrentPage) return
    if (kind === 'left-ellipsis') setCurrentPage(Math.max(1, safePage - 1))
    else if (kind === 'right-ellipsis') setCurrentPage(Math.min(totalPages, safePage + 1))
  }

  const handleLimitChange = (event) => {
    const next = parseInt(event.target.value, 10)
    setLimit(next)
    if (setPerPage) setPerPage(next)
    if (isControlledPage) onPageChange(0)
    else if (setCurrentPage) setCurrentPage(1)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        py: 1.25,
        px: 1,
        color: 'var(--table-text)',
      }}
    >
      <div className="flex gap-3 items-center">
        <Typography variant="body2" sx={{ color: 'var(--table-text)' }}>
          Showing{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {start}
          </Box>
          {' – '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {end}
          </Box>
          {' of '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {totalItems}
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--table-text)' }}>
            Rows per page:
          </Typography>
          <Select
            size="small"
            value={effectiveLimit}
            onChange={handleLimitChange}
            disabled={isLoading}
            sx={{
              fontSize: '0.8125rem',
              color: 'var(--table-text)',
              '& .MuiSelect-select': { py: 0.5, pr: '24px !important', pl: 1 },
              '& .MuiSelect-icon': { right: 3 },
              '& fieldset': { borderColor: 'var(--card-border)' },
            }}
          >
            {rowsPerPageOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8125rem' }}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </div>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <NavButton disabled={isLoading || safePage <= 1} onClick={() => goToPage(safePage - 1)}>
          Previous
        </NavButton>

        <Select
          size="small"
          value={safePage}
          onChange={(e) => goToPage(Number(e.target.value))}
          disabled={isLoading}
          sx={{
            fontSize: '0.8125rem',
            color: 'var(--table-text)',
            minWidth: 56,
            '& .MuiSelect-select': { py: 0.5, pr: '24px !important', pl: 1 },
            '& .MuiSelect-icon': { right: 3 },
            '& fieldset': { borderColor: 'var(--card-border)' },
          }}
        >
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <MenuItem key={p} value={p} sx={{ fontSize: '0.8125rem' }}>
              {p}
            </MenuItem>
          ))}
        </Select>

        {pageItems.map((item, idx) => {
          if (typeof item === 'string') {
            const isLeft = item === 'left-ellipsis'
            return (
              <PageButton
                key={`${item}-${idx}`}
                onClick={() => handleEllipsis(item)}
                disabled={isLeft ? safePage <= 2 : safePage >= totalPages - 3}
              >
                …
              </PageButton>
            )
          }
          const isActive = item === safePage
          return (
            <PageButton key={item} active={isActive} onClick={() => goToPage(item)}>
              {item}
            </PageButton>
          )
        })}

        <NavButton
          disabled={isLoading || safePage >= totalPages}
          onClick={() => goToPage(safePage + 1)}
        >
          Next
        </NavButton>

        {/* "Go to page" picker — the number buttons collapse to
            ellipsis past 7 pages, so the Select gives the user a direct
            way to jump to any page without having to chip away at Next. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--table-text)' }}>
            Go to page
          </Typography>
          <Select
            size="small"
            value={safePage}
            onChange={(e) => goToPage(Number(e.target.value))}
            disabled={isLoading}
            sx={{
              fontSize: '0.8125rem',
              color: 'var(--table-text)',
              minWidth: 56,
              '& .MuiSelect-select': { py: 0.5, pr: '24px !important', pl: 1 },
              '& .MuiSelect-icon': { right: 3 },
              '& fieldset': { borderColor: 'var(--card-border)' },
            }}
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: '0.8125rem' }}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </Box>
  )
}

export default PaginationFooter
